import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    if (form.password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: form.password,
      });
      setDone(true);
      setTimeout(() => navigate('/admin/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-400">Ungültiger Reset-Link.</p>
          <a href="#/admin/login" className="text-cyan-400 text-sm mt-4 block hover:underline">Zurück zum Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-1 select-none">
            <span className="text-2xl font-extrabold text-white uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Hosting-</span>
            <span className="text-2xl font-extrabold text-cyan-400 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Community</span>
          </a>
          <p className="text-sm text-slate-500 mt-2">Admin-Bereich</p>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8">
          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
              <h1 className="text-xl font-bold text-white">Passwort geändert</h1>
              <p className="text-sm text-slate-400">Du wirst in Kürze zum Login weitergeleitet...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <h1 className="text-xl font-bold text-white">Neues Passwort</h1>
                <p className="text-sm text-slate-500 mt-1">Wähle ein sicheres Passwort.</p>
              </div>

              {error && (
                <div className="px-4 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400" data-testid="reset-error">
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Neues Passwort</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    data-testid="new-password"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    placeholder="Min. 8 Zeichen"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Passwort bestätigen</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  required
                  data-testid="confirm-password"
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  placeholder="Passwort wiederholen"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit"
                className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Passwort speichern
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
